import { Controller } from '@hotwired/stimulus';
import {
  batchSetNodeKVPair,
  createNewContinueButtonInSection,
  DISPLAY_MODES,
  filterActionIfAlreadyExistsInDus,
  getDusFromDom,
  isPassiveRootCardSection,
  sectionHasContinueOrEquivalent,
  syncAllContinueButtonsForDocument,
} from '../../utils/case_experience/case_experience_in_rails';
import { CONTINUE_DISABLED_DOCUMENT_KINDS } from '../../utils/case_experience/case_experience_api';
import { prefetchCaseSections, restoreOfflineCaseSection } from '../../utils/case_experience/case_section_cache';

export default class extends Controller {
  static targets = ['section', 'continueButton'];
  static values = {
    documentId: Number,
    userId: Number,
    versionId: Number,
    documentKind: String,
    documentUserState: Object,
    isStudent: { type: Boolean, default: true },
  };

  fullViewCollapsedNodeUids = null;

  connect() {
    this._afterTurboStreamRender = this.afterTurboStreamRender.bind(this);
    document.addEventListener('turbo:after-stream-render', this._afterTurboStreamRender);
    this._refreshContinueButtonsBound = (e) => {
      const id = e.detail?.documentId;
      if (id != null && id !== this.documentIdValue) return;
      if (CONTINUE_DISABLED_DOCUMENT_KINDS.includes(this.documentKindValue)) return;
      void this.addContinueButtonsIfNeeded();
    };
    document.addEventListener('case-experience:refresh-continue-buttons', this._refreshContinueButtonsBound);

    this.initializeScrollTracking();
    this.loadScrollPosition();

    if (!CONTINUE_DISABLED_DOCUMENT_KINDS.includes(this.documentKindValue)) {
      void prefetchCaseSections(this.documentIdValue, this.userIdValue);
      void this.finishOfflineRestore();
    }
  }

  // Show the cached section after an offline reload. Otherwise add Continue buttons as usual.
  async finishOfflineRestore() {
    const restored = await restoreOfflineCaseSection({
      documentId: this.documentIdValue,
      userId: this.userIdValue,
    });
    if (!this.element?.isConnected || restored) return;
    await this.addContinueButtonsIfNeeded();
  }

  afterTurboStreamRender() {
    if (CONTINUE_DISABLED_DOCUMENT_KINDS.includes(this.documentKindValue)) return;
    clearTimeout(this._continueButtonsAfterStreamDebounce);
    this._continueButtonsAfterStreamDebounce = setTimeout(() => {
      if (!this.element?.isConnected) return;
      void this.addContinueButtonsIfNeeded();
    }, 50);
  }

  // Apply invisible/inactive properties from documentUserState to continue buttons
  applyContinueButtonDusFromState(buttons, dus) {
    if (!dus || !buttons?.length) return;
    buttons.forEach((button) => {
      const btnUid = button.id;
      const nodeDus = dus[btnUid];
      if (!nodeDus) return;
      if (nodeDus.invisible && nodeDus.invisible.value === true) {
        button.style.display = 'none';
      } else if (nodeDus.invisible && nodeDus.invisible.value === false) {
        button.style.display = '';
      }
      if (nodeDus.inactive && nodeDus.inactive.value === 2) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('inactive');
      } else if (nodeDus.inactive && nodeDus.inactive.value === 1) {
        button.disabled = false;
        button.setAttribute('aria-disabled', 'false');
        button.classList.remove('inactive');
      }
    });
  }

  // Synthetic Continue: regular progression cards only (never passive/informational roots).
  async addContinueButtonsIfNeeded() {
    let actions = [];
    for (const section of this.sectionTargets) {
      if (isPassiveRootCardSection(section)) {
        continue;
      }

      if (sectionHasContinueOrEquivalent(section)) {
        continue;
      }

      const inTwoColActivePane = !!section.closest('#case-experience-active-pane');
      if (!section.dataset?.aiDisplayFlag && !inTwoColActivePane) {
        continue;
      }

      actions.push(
        createNewContinueButtonInSection(section, this.documentIdValue, this.userIdValue),
      );
    }

    actions = actions.flat();
    actions = filterActionIfAlreadyExistsInDus(actions, this.documentUserStateValue);

    if (actions.length === 0) {
      this.applyContinueButtonDusFromState(this.continueButtonTargets, getDusFromDom());
      syncAllContinueButtonsForDocument(getDusFromDom());
      return;
    }

    const response = await batchSetNodeKVPair(
      actions,
      this.documentIdValue,
      this.userIdValue,
      this.application,
    );

    // batchSetNodeKVPair → updateDocumentUserState already runs Turbo.renderStreamMessage + merge DUS
    // when server sends turbo_streams; section HTML then carries correct state — skip stale-target DOM patch.
    const hadTurboStreams =
      typeof response?.turbo_streams === 'string' && response.turbo_streams.trim().length > 0;
    if (!hadTurboStreams) {
      this.applyContinueButtonDusFromState(this.continueButtonTargets, getDusFromDom());
    }

    syncAllContinueButtonsForDocument(getDusFromDom());
  }

  disconnect() {
    clearTimeout(this._continueButtonsAfterStreamDebounce);
    this._continueButtonsAfterStreamDebounce = null;
    if (this._refreshContinueButtonsBound) {
      document.removeEventListener('case-experience:refresh-continue-buttons', this._refreshContinueButtonsBound);
      this._refreshContinueButtonsBound = null;
    }
    if (this._afterTurboStreamRender) {
      document.removeEventListener('turbo:after-stream-render', this._afterTurboStreamRender);
      this._afterTurboStreamRender = null;
    }
    this.saveScrollPosition();
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  initializeScrollTracking() {
    // Track which sections have been viewed
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.markSectionAsVisited(entry.target);
          }
        });
      },
      {
        threshold: 0.5, // Section is considered viewed when 50% visible
        rootMargin: '0px',
      },
    );

    this.sectionTargets.forEach((section) => {
      this.observer.observe(section);
    });
  }

  markSectionAsVisited(section) {
    const nodeUid = section.dataset.nodeUid;
    if (!nodeUid) return;

    section.classList.add('visited');

    // Dispatch custom event for progress tracking
    this.dispatch('sectionVisited', {
      detail: { nodeUid: nodeUid },
    });
  }

  saveScrollPosition() {
    const scrollPosition = window.scrollY;
    sessionStorage.setItem(`case_scroll_${this.documentIdValue}`, scrollPosition);
  }

  loadScrollPosition() {
    const scrollPosition = sessionStorage.getItem(`case_scroll_${this.documentIdValue}`);
    if (scrollPosition !== null && scrollPosition !== '') {
      const parsed = parseInt(scrollPosition, 10);
      if (!isNaN(parsed) && parsed >= 0) {
        window.scrollTo(0, parsed);
      }
    }
  }

  changeViewMode(event) {
    const mode = event.target.value;
    if (!DISPLAY_MODES.includes(mode)) return;

    const docBody =
      event.target.closest('.doc-body') ||
      this.element.closest('.doc-body') ||
      this.element.querySelector('.doc-body');
    if (!docBody) return;

    const previousMode = DISPLAY_MODES.find((m) =>
      docBody.classList.contains(`${m}-display-mode`),
    );

    DISPLAY_MODES.forEach((m) => docBody.classList.remove(`${m}-display-mode`));
    docBody.classList.add(`${mode}-display-mode`);

    if (!this.isStudentValue) {
      const expandedModes = new Set(['full', 'outline']);
      const wasExpandedMode = expandedModes.has(previousMode);
      const isExpandedMode = expandedModes.has(mode);

      if (!wasExpandedMode && isExpandedMode) {
        this.expandSectionsForAdminFullView(docBody);
      }
      if (wasExpandedMode && !isExpandedMode) {
        this.restoreFullViewCollapsedSnapshot(docBody);
      }
    }
  }

  eachSectionCollapseControllerIn(docBody, fn) {
    const selector = '[data-controller~="case-experience--section-collapse"]';
    for (const el of docBody.querySelectorAll(selector)) {
      const c = this.application.getControllerForElementAndIdentifier(
        el,
        'case-experience--section-collapse',
      );
      if (c) fn(c, el);
    }
  }

  expandSectionsForAdminFullView(docBody) {
    const snapshot = (this.fullViewCollapsedNodeUids ??= new Set());
    snapshot.clear();
    this.eachSectionCollapseControllerIn(docBody, (c, el) => {
      if (c.stateValue?.collapsed !== true) return;
      const uid = c.nodeUidValue || el.dataset.uid;
      if (uid) snapshot.add(uid);
      c.stateValue = { ...c.stateValue, collapsed: false };
      c.updateUI();
    });
  }

  restoreFullViewCollapsedSnapshot(docBody) {
    const snapshot = this.fullViewCollapsedNodeUids;
    if (!snapshot?.size) return;
    this.eachSectionCollapseControllerIn(docBody, (c, el) => {
      const uid = c.nodeUidValue || el.dataset.uid;
      if (!uid || !snapshot.has(uid)) return;
      c.stateValue = { ...c.stateValue, collapsed: true };
      c.updateUI();
      c.stopTimeTracking();
    });
    snapshot.clear();
  }
}
